import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

interface StrategicPerformanceDashboardProps {
  data: {
    kpis: {
      total_workshops: number;
      completion_rate: number;
      total_savings: number;
      active_mentors: number;
      success_rate: number;
      avg_progress: number;
    };
    trends: Array<{
      month: string;
      workshops: number;
      completions: number;
      savings: number;
      mentors: number;
    }>;
    performance_metrics: {
      efficiency_score: number;
      quality_score: number;
      innovation_score: number;
      collaboration_score: number;
    };
  };
  loading?: boolean;
}

const StrategicPerformanceDashboard: React.FC<StrategicPerformanceDashboardProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Performance Dashboard</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Performance Dashboard</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No strategic performance data available
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPerformanceLevel = (score: number) => {
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Performance Dashboard</h3>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Workshops</p>
              <p className="text-2xl font-bold text-blue-900">{data.kpis.total_workshops}</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm">📊</span>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Completion Rate</p>
              <p className="text-2xl font-bold text-green-900">{data.kpis.completion_rate.toFixed(1)}%</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm">✅</span>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Total Savings</p>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(data.kpis.total_savings)}</p>
            </div>
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm">💰</span>
            </div>
          </div>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Active Mentors</p>
              <p className="text-2xl font-bold text-orange-900">{data.kpis.active_mentors}</p>
            </div>
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 text-sm">👥</span>
            </div>
          </div>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-indigo-600 font-medium">Success Rate</p>
              <p className="text-2xl font-bold text-indigo-900">{data.kpis.success_rate.toFixed(1)}%</p>
            </div>
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 text-sm">🎯</span>
            </div>
          </div>
        </div>
        
        <div className="bg-pink-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pink-600 font-medium">Avg Progress</p>
              <p className="text-2xl font-bold text-pink-900">{data.kpis.avg_progress.toFixed(1)}%</p>
            </div>
            <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
              <span className="text-pink-600 text-sm">📈</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Trends Chart */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Performance Trends</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.trends} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'workshops') return [`${value}`, 'Workshops'];
                  if (name === 'completions') return [`${value}`, 'Completions'];
                  if (name === 'savings') return [formatCurrency(value), 'Savings'];
                  if (name === 'mentors') return [`${value}`, 'Mentors'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="workshops"
                stackId="1"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.6}
                name="Workshops"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="completions"
                stackId="1"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.6}
                name="Completions"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="savings"
                stroke="#8b5cf6"
                strokeWidth={3}
                name="Savings"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Metrics</h4>
          <div className="space-y-3">
            {Object.entries(data.performance_metrics).map(([key, value]) => {
              const performance = getPerformanceLevel(value);
              const label = key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${value}%`, 
                          backgroundColor: performance.color 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium" style={{ color: performance.color }}>
                      {value.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Strategic Insights</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center">
                <span className="text-blue-600 text-sm mr-2">💡</span>
                <span className="text-sm text-blue-800">
                  {data.kpis.completion_rate > 70 ? 'Excellent completion rate indicates strong program execution' : 'Focus on improving completion rates for better outcomes'}
                </span>
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex items-center">
                <span className="text-green-600 text-sm mr-2">📈</span>
                <span className="text-sm text-green-800">
                  {data.kpis.total_savings > 100000 ? 'Significant savings achieved - program ROI is strong' : 'Continue focusing on high-impact workshops for better savings'}
                </span>
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex items-center">
                <span className="text-purple-600 text-sm mr-2">🎯</span>
                <span className="text-sm text-purple-800">
                  {data.kpis.active_mentors > 10 ? 'Strong mentor network supports knowledge sharing' : 'Consider expanding mentor network for better coverage'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicPerformanceDashboard;


